import { TransactionStatus } from './transaction-status';

export class Transaction<T> {
    private readonly _status: TransactionStatus = undefined;
    private _data: T = undefined;
    private readonly _error: string;

    constructor(status: TransactionStatus, data?: T, error?: string) {
        this._status = status;
        this._data = data;
        this._error = error;
    }

    set data(value: T) {
        this._data = value;
    }

    get data(): T {
        return this._data;
    }

    get status(): TransactionStatus {
        return this._status;
    }

    get error(): string {
        return this._error;
    }

    get isAuthError(): boolean {
        return !this.isSuccess && this._error === 'Forbidden';
    }

    get isSuccess(): boolean {
        return this.status === TransactionStatus.SUCCESS;
    }

    static success<T>(data?: T): Transaction<T> {
        return new Transaction(TransactionStatus.SUCCESS, data);
    }

    static error<T>(error: string = null): Transaction<T> {
        return new Transaction(TransactionStatus.ERROR, null, error);
    }

    flatten(): T {
        if (this.isSuccess) {
            return this.data;
        } else {
            throw new Error(this.error || 'Unknown error');
        }
    }

    fold(success: (data: T) => void, fail?: (throwable?: Error) => void): void {
        if (this.isSuccess) {
            success(this.data);
        } else if (!!fail && !this.isAuthError) {
            fail(new Error(this.error || 'Unknown error'));
        }
    }

    map<R>(success: (data: T) => R, fail?: (throwable?: Error) => R): R {
        if (this.isSuccess) {
            return success(this.data);
        } else if (!!fail) {
            return fail(new Error(this.error || 'Unknown error'));
        }
    }

    toString(): string {
        return (
            '[Transaction]\\n\\t|status=' +
            (this.status ? status.toString() : null) +
            '|\\n\\t|data=' +
            (this.data ? this.data.toString() : null) +
            '|'
        );
    }
}
